import { FolderOutlined } from "@mui/icons-material";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import InfoIcon from "@mui/icons-material/Info";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Chip, ListItemIcon, MenuItem, Stack, styled, Typography } from "@mui/material";
import { FC, useCallback, useContext, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { assetService, cacheService, mediaService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { FileCategory } from "../../../Core/Api/Types/file-category";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { RemoveFromResultObjectsById } from "../../../Redux/Reducers/redux-slice-asset";
import { routePaths } from "../../../Router/router";
import { DownloadFileFromUrl, UserHasAcl } from "../../../Utils/helpers";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import useTrashActions from "../../../Utils/Hooks/useTrash";
import AssetVideoResolutionChip from "../../Misc/Chips/AssetChip/asset-video-resolution-chip";
import MoreMenu from "../../Misc/MoreMenu";
import { ModalContext } from "../../Modals";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import ModalRenameObject, { ModalRenameObjectElementProps } from "../../Modals/ModalRenameObject/modal-rename-object";
import { useTranslation } from "react-i18next";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import { RemoveChildrenFromTreeObj, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import { RemoveObjs } from "../../../Redux/Reducers/redux-slice-drive";

export type AssetCardFooterProps = {
  obj: DbAssetObjectInfos;
  onNameChanged?: (name: string) => void;
  onClickInfo?: (obj: DbAssetObjectInfos) => void;
  shareBtn?: boolean;
  orderBtn?: boolean;
  renameBtn?: boolean;
  trashBtn?: boolean;
  infoBtn?: boolean;
  scale?: number;
  downloadBtn?: boolean;
  subtitle?: string;
};

const AssetCardFooter: FC<AssetCardFooterProps> = (props) => {
  const {
    obj,
    onNameChanged,
    onClickInfo,
    shareBtn,
    orderBtn,
    renameBtn,
    trashBtn,
    infoBtn,
    scale = GlobalConst.DEFAULT_SCALE,
    downloadBtn,
    subtitle,
  } = props;
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const dispatch = useDispatch();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const navigate = useNavigate();
  // Hooks
  const { addObjectByIdsToBasket } = useBasketActions();
  const { TrashObjectByIds } = useTrashActions();
  const collectionActions = useCollectionActions();

  // Handlers
  const handleAddToCollectionModal: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      collectionActions.AddItemsToCollection([obj]);
    },
    [obj.id]
  );

  const handleShare: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;

      openModal(ModalCreateShare, { object: obj });
    },
    [obj.id]
  );

  const handleOrder: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      addObjectByIdsToBasket([obj.id]);
    },
    [obj.id]
  );

  const handleTrash: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      TrashObjectByIds([obj.id], (id) => {
        dispatch(RemoveObjs([id]));
        dispatch(RemoveChildrenFromTreeObj({ children: [id], context: TreeContext.DriveTreeview }));
      });
    },
    [obj.id]
  );

  const handleInfo: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      onClickInfo && onClickInfo(props.obj);
    },
    [obj.id]
  );

  const handleGoToAsset: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      navigate(routePaths.asset.path + "/" + obj.id);
    },
    [obj.id]
  );

  const handleRename: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      console.log("rename");

      if (!openModal) return;

      openModal<ModalRenameObjectElementProps>(ModalRenameObject, {
        nameInitial: obj.name,
        objectId: obj.id,
        onConfirm: (name: string) => {
          console.log("rename asset", obj.id, name);
          onNameChanged && onNameChanged(name);
        },
      });
    },
    [obj.id, obj.name]
  );

  const handleDownload: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    // IF folder => download all children
    if (obj.type === ObjectType.Folder) {
      //assetService.GetChildrenByObjectId(obj.id, GlobalConst.MAX_DRIVE_DOWNLOADABLE_FILES).then((response) => {
      assetService.GetChildrenByObjectId(obj.id, 1000).then((response) => {
        if (response.success && response.children) {
          response.children
            .filter((x) =>
              [
                ObjectType.Video,
                ObjectType.Audio,
                ObjectType.Image,
                ObjectType.Document,
                ObjectType.ImageSequence,
                ObjectType.Subtitle,
              ].includes(x.type)
            )
            .map((child, index) => {
              fetch(
                mediaService.DownloadMediaFileByCategory(child.id, FileCategory.Native, cacheService.GetAuthKey()!),
                {
                  method: "HEAD",
                }
              )
                .then((response) => {
                  if (response.status === 200) {
                    DownloadFileFromUrl(
                      mediaService.DownloadMediaFileByCategory(
                        child.id,
                        FileCategory.Native,
                        cacheService.GetAuthKey()!
                      ),
                      ""
                    );
                  } else {
                    enqueueCustomSnackbar(t("alert:error.errorDownloadingAssetFile", { id: child.id }), "error");
                  }
                })
                .catch((error) => {
                  enqueueCustomSnackbar(t("alert:error.errorDownloadingAssetFile", { id: child.id }), "error");
                });
            });
        }
      });
    } else {
      window.open(
        mediaService.DownloadMediaFileByCategory(obj.id, FileCategory.Native, cacheService.GetAuthKey()!),
        "_blank"
      );
    }
  };

  // Rendering
  const _actionMenu = () => {
    return (
      <MoreMenu fadeIn className="action-menu" sx={{ height: "35px" }}>
        <MenuItem onClick={handleInfo}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          {t("common:infos")}
        </MenuItem>
        {downloadBtn && (
          <MenuItem onClick={handleDownload}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            {t("common:download")}
          </MenuItem>
        )}
        {shareBtn && (
          <MenuItem onClick={handleShare}>
            <ListItemIcon>
              <ShareOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t("common:share")}
          </MenuItem>
        )}
        <MenuItem onClick={handleAddToCollectionModal}>
          <ListItemIcon>
            <BookmarkBorderOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("button:addToCollection_one")}
        </MenuItem>
        {orderBtn && (
          <MenuItem onClick={handleOrder}>
            <ListItemIcon>
              <ShoppingBagOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t("common:order")}
          </MenuItem>
        )}
        {renameBtn && (
          <MenuItem onClick={handleRename}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            {t("common:rename")}
          </MenuItem>
        )}
        {UserHasAcl(AclTypes.aclSuperUser) && (
          <MenuItem onClick={handleGoToAsset}>
            <ListItemIcon>
              <FileOpenIcon fontSize="small" />
            </ListItemIcon>
            {t("menu:item.openAssetPage")}
          </MenuItem>
        )}
        {trashBtn && (
          <MenuItem onClick={handleTrash}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            {t("common:trash")}
          </MenuItem>
        )}
      </MoreMenu>
    );
  };

  const _renderChips = useMemo(() => {
    let duration: string | null = GetMetadataByTagName(obj, "durationSMPTE")?.value ?? null;
    let wrapper: string | null = GetMetadataByTagName(obj, "techWrapper")?.value ?? null;

    if (obj.type === ObjectType.Unknown) return <></>;

    if (duration) {
      if (duration.substring(0, 2) === "00") duration = `${duration.substring(3, 5)}:${duration.substring(6, 8)}`;
      else duration = `${parseInt(duration.substring(0, 2))}h${duration.substring(3, 5)}`;
    }

    return (
      <Stack direction="row" spacing={0.5} pt={0.5}>
        {obj.type === ObjectType.Folder || obj.type === ObjectType.Group ? (
          <Chip size="small" color="accent" variant="outlined" label={obj.childCount} icon={<FolderOutlined />} />
        ) : (
          <AssetTypeChip size="small" variant="outlined" label={wrapper ?? undefined} type={obj.type} />
        )}
        {duration ? <Chip size="small" label={duration} /> : <></>}
      </Stack>
    );
  }, [obj]);

  return (
    <CardBottom>
      <Stack direction="row" justifyContent="space-between" alignContent="center" alignItems="center">
        <Stack overflow={"hidden"} direction="column" gap={0.5}>
          <Typography
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            variant="body1"
            fontWeight={500}
          >
            {obj.name}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
              color="text.secondary"
              variant="body1"
            >
              {subtitle}
            </Typography>
          )}
          <Stack direction="row" justifyContent={"flex-start"} alignItems={"center"} gap={0.5}>
            {_renderChips}
          </Stack>
        </Stack>
      </Stack>
    </CardBottom>
  );
};

export default AssetCardFooter;

const CardBottom = styled("div")(({ theme }) => ({
  bottom: "0px",
  borderRadius: "0px 0px 6px 6px",
  minHeight: "15%",
  width: "calc(100% + 16px)",
  position: "relative",
  left: "-8px",
  padding: "8px 12px 8px 12px",
  alignContent: "center",
  alignItems: "center",
}));
